<template>
  <q-form @submit.prevent="goToNextStep">
    <div :class="[step === 1 ? 'q-mb-5x-md' : 'q-mb-md']">
      <p class="text-neutral text-xl q-mb-none">Welcome Back!</p>
      <p class="text-sm text-neutral-70 q-mb-none">
        Please login to your account
      </p>
    </div>

    <q-stepper
      ref="stepper"
      v-model="step"
      active-icon="none"
      class="bg-transparent"
      color="primary"
      header-class="display-none"
      alternative-labels
      flat
      keep-alive
    >
      <q-step
        icon="none"
        prefix="1"
        title="Request OTP"
        :done="step > 1"
        :name="1"
      >
        <div>
          <PInput
            ref="phoneNumberInput"
            v-model="phoneNumber"
            class="prepend-bg q-mb-lg"
            input-class="ellipsis"
            label="Phone Number"
            lazy-rules="ondemand"
            maxlength="10"
            placeholder="Enter your 10 digits phone number"
            type="tel"
            :validations="{
              length: 10,
              required: true,
              type: 'integer',
            }"
            @focus="phoneNumberInput.component.resetValidation()"
          >
            <template #prepend>
              <span class="text-sm">{{ isdCode }}</span>
            </template>
          </PInput>
        </div>
      </q-step>
      <q-step
        icon="none"
        prefix="2"
        title="Login"
        :class="[step === 2 ? 'q-pb-lg-xs' : 'q-pb-xxl']"
        :done="step > 2"
        :name="2"
      >
        <div>
          <p class="q-mb-none text-neutral-80">
            <span class="text-sm text-neutral-80">OTP is sent to:</span>
          </p>
          <div class="flex items-center">
            <span class="text-weight-medium text-neutral text-sm">
              {{ isdCode }}{{ phoneNumber }}
            </span>
            <PBtn
              class="q-ml-sm"
              label="Change number?"
              dense
              flat
              text
              @click="goToPreviousStep"
            />
          </div>

          <div class="flex justify-center q-my-2x-md">
            <OtpInput
              ref="otpInput"
              value=""
              input-type="number"
              separator=""
              @on-change="updateOtp"
              @on-complete="updateOtp"
              @keydown.enter="goToNextStep"
            />
          </div>
        </div>
      </q-step>
      <template #navigation>
        <q-stepper-navigation>
          <PBtn
            class="full-width"
            :label="step === 2 ? 'Login' : 'Continue'"
            :loading="isLoading"
            @click="goToNextStep"
          />
          <p v-if="step === 2" class="text-neutral-80 q-mt-xxl text-sm">
            Haven't got the OTP yet?
            <span v-if="requestForOtpTimer > 0" class="inline-block">
              Wait until
              <span
                class="rounded-borders bg-primary-30 text-neutral q-px-sm inline-block"
              >
                {{ requestForOtpTimer }}
              </span>
              secs
            </span>
            <PBtn
              v-else
              :loading="isRequestingForOtp"
              label="Resend OTP"
              dense
              flat
              text
              @click="requestOtp"
            />
          </p>
        </q-stepper-navigation>
      </template>
    </q-stepper>
  </q-form>
</template>

<script setup>
import { ENTRY_POINT } from "assets/scripts/utilities/constants";
import { notify } from "assets/scripts/utilities/api-clients";
import { useQuasar } from "quasar";
import { clearLoginAttemptInfo } from "supertokens-web-js/recipe/passwordless";

const $q = useQuasar();

const stepper = ref(null);
const step = ref(1);

const phoneNumberInput = ref(null);
const otpInput = ref(null);

const phoneNumber = ref("");
const isdCode = ref("+91");

const oneTimePassword = ref(null);

const updateOtp = (otp) => {
  oneTimePassword.value = otp;
};

const isRequestingForOtp = ref(false);
const isLoggingIn = ref(false);

const isLoading = computed(() => {
  return (
    (unref(step) === 1 && unref(isRequestingForOtp)) ||
    (unref(step) === 2 && unref(isLoggingIn))
  );
});

const authStore = useAuthStore();
const { isLoggedIn } = storeToRefs(useAuthStore());

const requestForOtpTimer = ref(59);
let requestForOtpTimerInterval = null;

const requestOtp = async () => {
  try {
    if (phoneNumberInput.value.component.validate()) {
      isRequestingForOtp.value = true;

      await authStore.requestOtp(unref(phoneNumber));

      clearInterval(requestForOtpTimerInterval);

      requestForOtpTimer.value = 59;

      requestForOtpTimerInterval = setInterval(() => {
        requestForOtpTimer.value -= 1;
      }, 1000);

      isRequestingForOtp.value = false;

      stepper.value.next();
    }
  } catch {
    isRequestingForOtp.value = false;
  }
};

const router = useRouter();

const login = async () => {
  try {
    if (unref(oneTimePassword)) {
      isLoggingIn.value = true;

      await authStore.login({
        otp: unref(oneTimePassword),
        phone: unref(phoneNumber),
      });

      if (unref(isLoggedIn)) {
        clearInterval(requestForOtpTimerInterval);

        $q.localStorage.remove("loginInfo");

        const redirectAfterLoginPath = $q.localStorage.getItem(
          "redirectAfterLoginPath",
        );

        const redirectPath =
          redirectAfterLoginPath?.length > 1
            ? redirectAfterLoginPath
            : ENTRY_POINT;

        $q.localStorage.remove("redirectAfterLoginPath");

        await router.push(redirectPath);

        notify({
          message: "Logged in successfully!",
          type: "positive",
        });
      }
    } else {
      const type = "negative";

      notify({
        type,
        message: "OTP is required for logging in",
      });
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoggingIn.value = false;
  }
};

const goToNextStep = () => {
  if (step.value === 1) {
    requestOtp();
  } else if (step.value === 2) {
    login();
  }
};

const goToPreviousStep = async () => {
  await clearLoginAttemptInfo();

  unref(otpInput).clearInput();

  unref(stepper).previous();
};

onBeforeMount(() => {
  const loginInfo = $q.localStorage.getItem("loginInfo");

  if (loginInfo) {
    phoneNumber.value = loginInfo.phoneNumber;

    step.value = loginInfo.step;
  }
});

watch([phoneNumber, step], ([phoneNumber, step]) => {
  $q.localStorage.set("loginInfo", {
    phoneNumber: phoneNumber,
    step: step,
  });
});
</script>

<style lang="scss" scoped>
.q-stepper {
  :deep(.q-stepper__step-inner) {
    padding: 0;
  }

  :deep(.q-stepper__nav) {
    padding: 0;
  }
}
</style>
